'use client';

/**
 * NotificationCenter Component
 *
 * Main notification dropdown component that replaces the mock notifications
 * in AppHeader. Displays notification list with filtering and actions.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Bell, X, Check, Settings, Loader2, RefreshCw } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectNotifications,
  selectActionableUnreadCount,
  selectIsDropdownOpen,
  selectIsLoading,
  selectActiveFilter,
  selectHasMore,
  selectError,
  setDropdownOpen,
  setActiveFilter,
} from '@/store/slices/notificationsSlice';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification, NotificationCategory } from '@/store/slices/notificationsTypes';
import { NOTIFICATION_CATEGORIES } from '@/store/slices/notificationsTypes';
import { NotificationItem } from './NotificationItem';
import { NotificationBadge, NotificationDot } from './NotificationBadge';
import { cn } from '@/lib/utils';

interface NotificationCenterProps {
  /** Compact mode for smaller headers (e.g., conference) */
  compact?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when notification is clicked */
  onNotificationClick?: (notification: Notification) => void;
}

export function NotificationCenter({
  compact = false,
  className,
  onNotificationClick,
}: NotificationCenterProps) {
  const dispatch = useAppDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Selectors
  const notifications = useAppSelector(selectNotifications);
  const actionableUnreadCount = useAppSelector(selectActionableUnreadCount);
  const isOpen = useAppSelector(selectIsDropdownOpen);
  const isLoading = useAppSelector(selectIsLoading);
  const activeFilter = useAppSelector(selectActiveFilter);
  const hasMore = useAppSelector(selectHasMore);
  const error = useAppSelector(selectError);

  // Hook for refresh functionality
  const {
    refresh,
    markAsRead: persistMarkAsRead,
    markAllAsRead: persistMarkAllAsRead,
    archive: persistArchive,
  } = useNotifications({ autoFetch: false });

  // Filter notifications based on active filter
  const filteredNotifications = activeFilter === 'all'
    ? notifications
    : notifications.filter((n) => n.category === activeFilter);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        dispatch(setDropdownOpen(false));
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, dispatch]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        dispatch(setDropdownOpen(false));
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, dispatch]);

  const handleToggle = useCallback(() => {
    dispatch(setDropdownOpen(!isOpen));
  }, [dispatch, isOpen]);

  const handleClose = useCallback(() => {
    dispatch(setDropdownOpen(false));
  }, [dispatch]);

  const handleArchive = useCallback(
    (id: string) => {
      void persistArchive(id);
    },
    [persistArchive]
  );

  const handleMarkAllRead = useCallback(() => {
    void persistMarkAllAsRead();
  }, [persistMarkAllAsRead]);

  const handleFilterChange = useCallback(
    (filter: NotificationCategory | 'all') => {
      dispatch(setActiveFilter(filter));
    },
    [dispatch]
  );

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        void persistMarkAsRead(notification.id);
      }
      if (onNotificationClick) {
        onNotificationClick(notification);
      }
      // Open the notification's target page in a new window/tab
      if (notification.actionUrl) {
        window.open(notification.actionUrl, '_blank', 'noopener');
      }
      handleClose();
    },
    [persistMarkAsRead, onNotificationClick, handleClose]
  );

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className={cn(
          'p-2 rounded-lg text-white/70 hover:text-white',
          'hover:bg-white/[0.06] transition-colors relative group'
        )}
        aria-label={`Notifications${actionableUnreadCount > 0 ? ` (${actionableUnreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div
          className={cn(
            'p-2 rounded-lg transition-all duration-200',
            'bg-amber-500/10 group-hover:scale-105'
          )}
        >
          <Bell className="w-5 h-5 text-amber-400" />
        </div>

        {/* Badge - shows count when there are actionable unread notifications */}
        {actionableUnreadCount > 0 && (
          <NotificationBadge
            count={actionableUnreadCount}
            size="sm"
            className="absolute top-0 right-0"
            animate={true}
          />
        )}
      </button>

      {/* Dropdown Panel - Consistent styling regardless of compact mode */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-2 bg-[#0a0a0a] border border-amber-500/20',
            'rounded-xl shadow-2xl overflow-hidden z-50',
            'w-[28rem]', // Always use full width for consistency
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150 [animation-fill-mode:backwards]'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-sm font-semibold text-white">
                Notifications
              </span>
              {actionableUnreadCount > 0 && (
                <NotificationBadge count={actionableUnreadCount} size="sm" />
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Mark all read button */}
              {actionableUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className={cn(
                    'p-1.5 rounded text-white/70 hover:text-white',
                    'hover:bg-white/[0.06] transition-colors'
                  )}
                  title="Mark all as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}

              {/* Settings link */}
              <Link
                href="/settings/ui/notifications"
                className={cn(
                  'p-1.5 rounded text-white/70 hover:text-white',
                  'hover:bg-white/[0.06] transition-colors'
                )}
                title="Notification settings"
                onClick={handleClose}
              >
                <Settings className="w-4 h-4" />
              </Link>

              {/* Close button */}
              <button
                onClick={handleClose}
                className={cn(
                  'p-1 rounded text-white/70 hover:text-white',
                  'hover:bg-white/[0.06] transition-colors'
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="px-4 py-2 text-sm bg-red-500/10 border-b border-red-500/20 flex items-center justify-between">
              <span className="text-red-400 truncate flex-1">{error}</span>
              <button
                onClick={refresh}
                className="ml-2 p-1 rounded text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-colors flex items-center gap-1"
                title="Retry"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span className="text-xs">Retry</span>
              </button>
            </div>
          )}

          {/* Filter Tabs - Always shown for consistency */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06] overflow-x-auto">
            <FilterTab
              label="All"
              isActive={activeFilter === 'all'}
              onClick={() => handleFilterChange('all')}
            />
            {NOTIFICATION_CATEGORIES.slice(0, 5).map((cat) => (
              <FilterTab
                key={cat.id}
                label={cat.label}
                isActive={activeFilter === cat.id}
                onClick={() => handleFilterChange(cat.id)}
              />
            ))}
          </div>

          {/* Notification List - Consistent height */}
          <div className="overflow-y-auto max-h-96">
            {isLoading && filteredNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <EmptyState filter={activeFilter} />
            ) : (
              filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onArchive={handleArchive}
                  onClick={handleNotificationClick}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <Link
              href="/notifications"
              target="_blank"
              rel="noopener"
              className={cn(
                'block w-full text-center text-sm text-amber-400',
                'hover:text-amber-300 transition-colors'
              )}
              onClick={handleClose}
            >
              View All Notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Filter tab component
function FilterTab({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs font-medium rounded-md whitespace-nowrap',
        'transition-colors',
        isActive
          ? 'bg-amber-500/20 text-amber-400'
          : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
      )}
    >
      {label}
    </button>
  );
}

// Empty state component
function EmptyState({ filter }: { filter: NotificationCategory | 'all' }) {
  const categoryMeta = filter !== 'all'
    ? NOTIFICATION_CATEGORIES.find((c) => c.id === filter)
    : null;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="p-4 rounded-full bg-white/[0.04] mb-3">
        <Bell className="w-8 h-8 text-slate-500" />
      </div>
      <p className="text-sm text-white/70">
        {filter === 'all'
          ? 'No notifications yet'
          : `No ${categoryMeta?.label.toLowerCase() || filter} notifications`}
      </p>
      <p className="text-xs text-white/60 mt-1">
        New notifications will appear here
      </p>
    </div>
  );
}

export default NotificationCenter;
